/**
 * F28 (Wave 2C-ii) — "Add email suppression" modal.
 *
 * Composes Modal + FormField + TextInput (email) + Textarea (reason + notes).
 * Client-side validation via the shared
 * `AddEmailSuppressionInputSchema` from @bravemove/types so the dialog can not
 * submit obviously-invalid data; the server is still the source of truth on
 * rate limits. Adding an already-suppressed address is NOT an error — the
 * server UPSERTs (refreshing reason/source/notes) and returns
 * `created: false`; the parent page toasts the "already suppressed —
 * updated" variant (NT-07).
 *
 * Gated by `email_suppressions:write` (super_admin + support only) — the
 * parent page hides the Add button entirely for content_moderator.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, TextInput, Textarea } from '@bravemove/ui-web';
import { AddEmailSuppressionInputSchema, type AddEmailSuppressionInput } from '@bravemove/types';

export interface AddSuppressionDialogProps {
  readonly open: boolean;
  readonly onConfirm: (input: AddEmailSuppressionInput) => Promise<void>;
  readonly onClose: () => void;
}

export function AddSuppressionDialog({ open, onConfirm, onClose }: AddSuppressionDialogProps) {
  const { t } = useI18n();
  const [emailAddress, setEmailAddress] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setEmailAddress('');
    setReason('');
    setNotes('');
    setServerError(null);
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(() => {
    const candidate = {
      emailAddress: emailAddress.trim(),
      reason: reason.trim(),
      // Pass undefined so the optional() in the schema accepts it.
      notes: notes.trim() === '' ? undefined : notes.trim(),
    };
    return AddEmailSuppressionInputSchema.safeParse(candidate);
  }, [emailAddress, reason, notes]);

  const disableSubmit = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setServerError(null);
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } catch {
      // Duplicate adds are NOT errors (server UPSERTs + reports created:false
      // — the parent toasts the variant); anything landing here is a real
      // failure (network / 403 / rate limit).
      setServerError(
        t('admin:email_suppressions.add.error_generic', {
          defaultValue: 'Could not add suppression. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:email_suppressions.add.dialog_title', {
        defaultValue: 'Add email suppression',
      })}
      description={t('admin:email_suppressions.add.dialog_description', {
        defaultValue:
          'BraveMove will not send email to this address until it is removed. This action is audit-logged.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="add-suppression-cancel"
          >
            {t('admin:email_suppressions.add.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="add-suppression-confirm"
          >
            {t('admin:email_suppressions.add.submit', { defaultValue: 'Suppress address' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="add-suppression-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField
          label={t('admin:email_suppressions.add.email_label', { defaultValue: 'Email address' })}
          helper={t('admin:email_suppressions.add.email_helper', {
            defaultValue: 'Lowercased before save. Must be a valid email.',
          })}
          required
        >
          <TextInput
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder={t('admin:email_suppressions.add.email_placeholder', {
              defaultValue: 'user@example.com',
            })}
            data-testid="add-suppression-email"
            aria-label={t('admin:email_suppressions.add.email_label', {
              defaultValue: 'Email address',
            })}
          />
        </FormField>

        <FormField
          label={t('admin:email_suppressions.add.reason_label', { defaultValue: 'Reason' })}
          helper={t('admin:email_suppressions.add.reason_helper', {
            defaultValue: 'Visible to other admins. 1–500 characters.',
          })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="add-suppression-reason"
            aria-label={t('admin:email_suppressions.add.reason_label', { defaultValue: 'Reason' })}
          />
        </FormField>

        <FormField
          label={t('admin:email_suppressions.add.notes_label', {
            defaultValue: 'Notes (optional)',
          })}
          helper={t('admin:email_suppressions.add.notes_helper', {
            defaultValue: 'Internal context for the support team. Up to 1000 characters.',
          })}
        >
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={1000}
            data-testid="add-suppression-notes"
            aria-label={t('admin:email_suppressions.add.notes_label', {
              defaultValue: 'Notes (optional)',
            })}
          />
        </FormField>

        {serverError ? (
          <div data-testid="add-suppression-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default AddSuppressionDialog;
